import { ASSET_RESOURCE_LINK, ASSET_RESOURCE_MIME_LINK, parseAssetReferences } from '@common/entity-content'
import type { SnapshotEntity } from 'runtimes-shared'

type CollectParams = {
  getEntitiesById: (entityIds: string[]) => Promise<SnapshotEntity[]>
  readResourceText: (resourceId: string) => Promise<string | undefined>
  maxDepth?: number
}

const TEXTUAL_MIME = /^text\/|json|ya?ml|xml|markdown|csv/i

export const isTextualPayload = (mimeType: string | undefined): boolean => !!mimeType && TEXTUAL_MIME.test(mimeType)

const referencedIds = (payload: string): string[] => [
  ...new Set(parseAssetReferences(payload).map((reference) => reference.entityId)),
]

// Walks `asset://` references outwards from a payload — a syllabus reaches its pages, and each page its
// images — so a preview can resolve the same closure a published snapshot bakes in.
const collectReferencedEntities = async (
  payload: string,
  { getEntitiesById, readResourceText, maxDepth = 3 }: CollectParams
): Promise<SnapshotEntity[]> => {
  const collected = new Map<string, SnapshotEntity>()
  let frontier = referencedIds(payload)

  for (let depth = 0; depth < maxDepth && frontier.length > 0; depth += 1) {
    const fetched = await getEntitiesById(frontier)
    const payloads = await Promise.all(
      fetched.map(async (entity) => {
        collected.set(entity.entityId, entity)
        const resourceId = entity.links[ASSET_RESOURCE_LINK]
        // Only text can carry further references, so image bytes are never read just to scan them.
        if (!resourceId || !isTextualPayload(entity.links[ASSET_RESOURCE_MIME_LINK])) return undefined
        return readResourceText(resourceId)
      })
    )
    frontier = [...new Set(payloads.filter(Boolean).flatMap((text) => referencedIds(text!)))].filter(
      (id) => !collected.has(id)
    )
  }

  return [...collected.values()]
}

export default collectReferencedEntities
